import { useState } from 'react'

/**
 * Черновик формы поверх серверного значения.
 *
 * Зачем это вообще есть. Обычный способ наполнить форму данными —
 * `useEffect(() => setForm(entity), [entity])`. В панели он не работает: данные
 * живые. `bot.state` прилетает по WS каждые пару секунд и патчит кэш
 * (`lib/ws-cache.ts`), инвалидация после любой мутации перечитывает список — и
 * каждый раз это НОВЫЙ объект. Эффект видит смену ссылки, перезаписывает форму
 * серверным значением, и человек, который печатает новое имя, теряет введённое
 * на полуслове вместе с позицией курсора. Именно этот баг («начинаешь писать,
 * а имя стирается назад») и лечит хук.
 *
 * Правило разрешения конфликта — то же, что в `screens/bots/behaviours-model.ts`
 * (`mergeServerUpdate`), но подробнее и переиспользуемо: сравнение идёт ПОЛЕ ЗА
 * ПОЛЕМ. Поле, которое человек не трогал, продолжает подхватывать сервер (форма
 * не показывает устаревшее); поле, которое отличается от прошлого серверного
 * значения, считается изменённым руками, и сервер его не перетирает. Так
 * переименование не мешает форме узнать, что бота, скажем, перевели на другой
 * сервер из соседней вкладки.
 *
 * Сравнение структурное, а не по ссылке: серверное значение обычно собирается
 * прямо в рендере (`formOf(server)`), и по ссылке оно «менялось» бы каждый раз.
 */

/**
 * Метка «это другая форма».
 *
 * Смена метки — единственный случай, когда черновик берётся с сервера
 * безусловно, вместе с уже введённым. Диалоги живут в дереве постоянно, поэтому
 * метка — это «какую сущность открыли» (`open ? id : null`): открыли другого
 * бота или закрыли окно — форма обязана начаться заново.
 */
export type DraftSeed = string | number | boolean | null

export interface Draft<T> {
  /** Что показывать в полях. */
  value: T
  /** Заменить черновик целиком (или функцией от предыдущего). */
  set: (next: T | ((previous: T) => T)) => void
  /**
   * Правка части полей. Считается ОТ ПРЕДЫДУЩЕГО состояния, а не от значения из
   * замыкания: два изменения в одном тике иначе перетирают друг друга.
   */
  patch: (part: Partial<T> | ((previous: T) => Partial<T>)) => void
  /** Черновик расходится с текущим серверным значением. */
  dirty: boolean
  /**
   * Вернуть форму к серверному значению: «Отмена» и момент после успешного
   * сохранения.
   *
   * Своего значения не принимает намеренно: точка отсчёта у формы одна — то,
   * что сейчас в `server`. «Сохранить как» с подставленным ответом сервера
   * рассинхронизировало бы её с кэшем ровно до первого фонового обновления,
   * которое всё равно вернуло бы кэш.
   */
  reset: () => void
}

export function useDraft<T>(server: T, seed: DraftSeed = null): Draft<T> {
  const [value, setValue] = useState<T>(server)
  /** Серверное значение, которое форма уже «видела»: точка отсчёта «трогали ли поле». */
  const [base, setBase] = useState<T>(server)
  const [seenSeed, setSeenSeed] = useState<DraftSeed>(seed)

  // Правка состояния в рендере, а не в эффекте. Эффект дал бы лишний кадр со
  // старым значением (видно как мигание в полях при открытии диалога), а React
  // штатно поддерживает такую подстройку: setState своего же компонента в
  // рендере просто перезапускает рендер, не доходя до коммита.
  let current = value
  if (!Object.is(seed, seenSeed)) {
    setSeenSeed(seed)
    setBase(server)
    setValue(server)
    current = server
  } else if (!equalDeep(base, server)) {
    const merged = mergeUntouched(value, base, server)
    setBase(server)
    if (!Object.is(merged, value)) setValue(merged)
    current = merged
  }

  return {
    value: current,
    set: next =>
      setValue(previous => (typeof next === 'function' ? asUpdater(next)(previous) : next)),
    patch: part =>
      setValue(previous => ({
        ...previous,
        ...(typeof part === 'function' ? asPartialUpdater(part)(previous) : part)
      })),
    dirty: !equalDeep(current, server),
    reset: () => {
      setBase(server)
      setValue(server)
    }
  }
}

/**
 * Сужение `T | ((prev: T) => T)` до функции.
 *
 * Отдельные помощники, а не приведение по месту: `T` сам может оказаться
 * функцией только теоретически, но TypeScript этого не знает, и без сужения
 * пришлось бы писать `as`, которого в проекте не хотят.
 */
function asUpdater<T>(next: T | ((previous: T) => T)): (previous: T) => T {
  return next as (previous: T) => T
}

function asPartialUpdater<T>(
  part: Partial<T> | ((previous: T) => Partial<T>)
): (previous: T) => Partial<T> {
  return part as (previous: T) => Partial<T>
}

/**
 * Слияние серверного обновления с черновиком, поле за полем.
 *
 * `draft` — что в полях сейчас, `base` — серверное значение на момент прошлой
 * синхронизации, `next` — что пришло сейчас. Поле берётся с сервера, только
 * если черновик по нему совпадает с `base`, то есть его не правили руками.
 *
 * Значение не-объект (строка, число) сливается целиком — делить там нечего.
 */
export function mergeUntouched<T>(draft: T, base: T, next: T): T {
  if (!isRecord(draft) || !isRecord(base) || !isRecord(next)) {
    return equalDeep(draft, base) ? next : draft
  }

  const out: Record<string, unknown> = { ...draft }
  let changed = false

  for (const key of Object.keys(next)) {
    if (equalDeep(out[key], next[key])) continue
    if (!equalDeep(out[key], base[key])) continue
    out[key] = next[key]
    changed = true
  }

  // Ключ, которого на сервере больше нет. Такое бывает у форм с необязательными
  // частями — молча оставить старое значение значило бы отправить его обратно.
  for (const key of Object.keys(out)) {
    if (key in next) continue
    if (!equalDeep(out[key], base[key])) continue
    delete out[key]
    changed = true
  }

  return changed ? (out as T) : draft
}

/**
 * Структурное сравнение. Нужно именно оно: серверное значение формы собирается
 * в рендере заново, и сравнение по ссылке считало бы изменением каждый рендер.
 *
 * Глубина ограничена тем, что реально лежит в формах панели: примитивы,
 * массивы, простые объекты (`ServerHook[]`, `config` правила). `Date`, `Map` и
 * классы сюда не попадают — если попадут, сравнение честно скажет «разные».
 */
export function equalDeep(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
    return a.every((item, index) => equalDeep(item, b[index]))
  }

  if (!isRecord(a) || !isRecord(b)) return false
  const keys = Object.keys(a)
  if (keys.length !== Object.keys(b).length) return false
  return keys.every(key => key in b && equalDeep(a[key], b[key]))
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
